/**
 * Pure classification of the errors the Twilio Voice SDK emits, so the softphone tells a call
 * that simply ended (refused, busy, unanswered) and a network blip the SDK heals by itself apart
 * from a genuine failure.
 *
 * Twilio wraps the SIP answer of a hangup: without `enableImprovedSignalingErrorPrecision`, a
 * "Decline" reaches us as a 31005 ConnectionError whose `originalError.code` is 31603; with it,
 * as a 31603 error. Both shapes are read.
 */

interface TwilioErrorLike {
  code?: unknown;
  message?: unknown;
  originalError?: unknown;
}

function asErrorLike(err: unknown): TwilioErrorLike | null {
  return err && typeof err === 'object' ? (err as TwilioErrorLike) : null;
}

function numericCode(value: unknown): number | null {
  const e = asErrorLike(value);
  return e && typeof e.code === 'number' ? e.code : null;
}

function messageOf(value: unknown): string {
  const e = asErrorLike(value);
  return e && typeof e.message === 'string' ? e.message : '';
}

/**
 * SIP answers that end a call normally, with the label the softphone shows (Twilio Voice SDK
 * error reference: 31480 TemporarilyUnavailable, 31486 BusyHere, 31603 Decline).
 */
const NORMAL_END_LABELS: ReadonlyMap<number, string> = new Map([
  [31480, 'Pas de réponse'],
  [31486, 'Ligne occupée'],
  [31603, 'Appel refusé'],
]);

export type CallErrorOutcome = { kind: 'ended'; label: string } | { kind: 'failure' };

/** Whether a call 'error' is the other side ending the call (refused, busy, no answer) or a failure. */
export function classifyCallError(err: unknown): CallErrorOutcome {
  const e = asErrorLike(err);
  if (!e) return { kind: 'failure' };
  for (const code of [numericCode(e.originalError), numericCode(e)]) {
    const label = code === null ? undefined : NORMAL_END_LABELS.get(code);
    if (label) return { kind: 'ended', label };
  }
  return { kind: 'failure' };
}

/**
 * Signaling/transport drops the SDK reconnects by itself: 31005 ConnectionError ("signaling
 * websocket unexpectedly ended") and 31009 TransportError ("no transport").
 */
const TRANSIENT_CONNECTION_CODES = new Set([31005, 31009]);

/** 31000 is Twilio's catch-all; it is a network drop only when the websocket itself failed. */
const SOCKET_ERROR = /socket error/i;

/**
 * True when a device 'error' is a transient loss of connectivity that self-heals: a known
 * transport code, a 31000 raised by the websocket, or any error while the browser is offline.
 */
export function isTransientDeviceError(err: unknown, online: boolean = typeof navigator === 'undefined' || navigator.onLine !== false): boolean {
  if (!online) return true;
  const code = numericCode(err);
  if (code === null) return false;
  if (TRANSIENT_CONNECTION_CODES.has(code)) return true;
  const e = asErrorLike(err);
  return code === 31000 && (SOCKET_ERROR.test(messageOf(err)) || SOCKET_ERROR.test(messageOf(e?.originalError)));
}
